import React, { createContext, useState, useContext, ReactNode, useEffect, useRef, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { firestore } from '../firebase';
import { normalizeInstagramHandle } from '../services/verificationService';

interface InstagramVerificationContextProps {
  isVerified: boolean;
  isChecking: boolean;
  /**
   * The record for this handle exists but belongs to someone else, so the code we showed can never
   * verify. The document is keyed by handle alone and is also written by the browser during signup,
   * so a signup claiming the same handle can replace a reservation mid-flight.
   *
   * Always false when no `expectedInfluencerID` was given — signup has no reservation to lose.
   */
  hasOwnershipConflict: boolean;
  currentHandle: string | null;
  watchInstagramHandle: (instagramHandle: string, expectedInfluencerID?: string) => void;
  stopWatching: () => void;
}

const InstagramVerificationContext = createContext<InstagramVerificationContextProps | undefined>(undefined);

export const useInstagramVerification = () => {
  const context = useContext(InstagramVerificationContext);
  if (!context) {
    throw new Error('useInstagramVerification must be used within an InstagramVerificationProvider');
  }
  return context;
};

interface InstagramVerificationProviderProps {
  children: ReactNode;
}

export const InstagramVerificationProvider: React.FC<InstagramVerificationProviderProps> = ({ children }) => {
  const [isVerified, setIsVerified] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [hasOwnershipConflict, setHasOwnershipConflict] = useState(false);
  const [currentHandle, setCurrentHandle] = useState<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  /**
   * Watches the proof record for a handle.
   *
   * Pass `expectedInfluencerID` when the proof has to belong to a specific creator — a
   * verified record for a handle says the handle was proven, not that *this* creator proved
   * it. Records created for a handle change carry the creator's ID; signup records predate
   * it, so omitting the argument keeps the signup flow behaving as before.
   */
  const watchInstagramHandle = useCallback((instagramHandle: string, expectedInfluencerID?: string) => {
    // Clean up existing listener
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    setCurrentHandle(instagramHandle);
    setIsChecking(true);
    setHasOwnershipConflict(false);
    // Nothing ever calls stopWatching, so the previous handle's verified=true would otherwise
    // survive into the next change and show its success screen before the first snapshot lands.
    setIsVerified(false);

    const handleKey = normalizeInstagramHandle(instagramHandle);
    const verificationRef = doc(firestore, "instagramVerifications", handleKey);
    
    const listener = onSnapshot(verificationRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        const belongsToCreator = !expectedInfluencerID || data.influencerID === expectedInfluencerID;
        setIsVerified(!!data.verified && belongsToCreator);
        setHasOwnershipConflict(!!expectedInfluencerID && !belongsToCreator);
      } else {
        setIsVerified(false);
        // A missing record is a normal transient before the reservation write lands, not a conflict.
        setHasOwnershipConflict(false);
      }
      setIsChecking(false);
    }, (error) => {
      console.error('Error listening to Instagram verification:', error);
      setIsChecking(false);
      setIsVerified(false);
      setHasOwnershipConflict(false);
    });

    unsubscribeRef.current = listener;
  }, []);

  const stopWatching = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    setCurrentHandle(null);
    setIsChecking(false);
    setIsVerified(false);
    setHasOwnershipConflict(false);
  }, []);

  return (
    <InstagramVerificationContext.Provider 
      value={{ 
        isVerified, 
        isChecking,
        hasOwnershipConflict,
        currentHandle,
        watchInstagramHandle,
        stopWatching
      }}
    >
      {children}
    </InstagramVerificationContext.Provider>
  );
}; 